import React, { useState, useCallback, useEffect, useRef } from 'react';
import { UserUpload, useUserUploads } from '../hooks/useUserUploads';
import { useNavigate } from 'react-router-dom';

interface UserUploadsGalleryProps {
  onRefresh?: () => void;
}

const UserUploadsGallery: React.FC<UserUploadsGalleryProps> = ({ onRefresh }) => {
  const { uploads, loading, error, deleteImage, refreshUploads } = useUserUploads();
  const [selectedImage, setSelectedImage] = useState<UserUpload | null>(null);
  const navigate = useNavigate();
  const [columns, setColumns] = useState<UserUpload[][]>([]);
  const [columnCount, setColumnCount] = useState(3);
  
  const handleRefresh = () => {
    refreshUploads();
    if (onRefresh) {
      onRefresh();
    }
  };
  
  const handleDelete = async (upload: UserUpload) => {
    if (window.confirm(`Are you sure you want to delete ${upload.filename}?`)) {
      try {
        // The upload.id contains the full path including userId - pass the complete ID
        const success = await deleteImage(upload.id);
        
        if (success) {
          // If the delete was successful and we have a modal open with this image
          if (selectedImage?.id === upload.id) {
            setSelectedImage(null);
          }
          
          // Refresh the uploads after successful deletion
          handleRefresh();
        } else {
          console.error("Failed to delete image:", upload.id);
          alert("Failed to delete image. Please try again.");
        }
      } catch (err) {
        console.error("Error deleting image:", err);
        alert("An error occurred while deleting the image.");
      }
    }
  };
  
  const handleImageClick = (upload: UserUpload) => {
    setSelectedImage(upload);
  };
  
  const handleTryOn = (upload: UserUpload, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent opening the modal
    navigate('/try-on', { state: { selectedPhoto: upload } });
  };
  
  // Close the modal
  const closeModal = () => {
    setSelectedImage(null);
  };
  
  // Create columns for masonry layout based on image aspect ratios
  const distributeImagesIntoColumns = useCallback(() => {
    if (uploads.length === 0) return;
    
    // Create empty columns
    const newColumns: UserUpload[][] = Array.from(
      { length: columnCount },
      () => [],
    );
    
    // Track the total "height" of each column
    const columnHeights = Array(columnCount).fill(0);
    
    // Distribute images by always adding to the shortest column
    uploads.forEach((upload) => {
      // Find the shortest column
      const shortestColumnIndex = columnHeights.indexOf(
        Math.min(...columnHeights),
      );
      
      // Add image to that column
      newColumns[shortestColumnIndex].push(upload);
      
      // Update the column height - assume square aspect ratio if unknown
      columnHeights[shortestColumnIndex] += 1;
    });
    
    setColumns(newColumns);
  }, [uploads, columnCount]);
  
  // Update columns when images change
  useEffect(() => {
    distributeImagesIntoColumns();
  }, [distributeImagesIntoColumns]);
  
  // Update columns when window resizes
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      let count = 2; // Default for mobile
      
      if (width >= 1200) {
        count = 4; // Extra large screens
      } else if (width >= 992) {
        count = 3; // Large screens
      } else if (width >= 768) {
        count = 3; // Medium screens
      } else if (width < 480) {
        count = 2; // Small mobile screens
      }
      
      setColumnCount(count);
    };
    
    handleResize(); // Initial call
    window.addEventListener("resize", handleResize);
    
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);
  
  // Empty state - will be handled by the parent component
  if (loading && uploads.length === 0) {
    return (
      <div className="flex justify-center items-center py-10">
        <div className="spinner"></div>
      </div>
    );
  }
  
  if (uploads.length === 0 && !loading) {
    return null; // Empty state will be handled by parent
  }
  
  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Error message */}
      {error && (
        <div className="p-3 sm:p-4 bg-haus-dark-brown/10 text-haus-dark-brown text-sm sm:text-base rounded-lg">
          {error}
        </div>
      )}
      
      {/* Bento Grid Layout */}
      <div className="flex gap-2 xs:gap-3 sm:gap-4 w-full">
        {columns.map((column, columnIndex) => (
          <div
            key={columnIndex}
            className="flex-1 flex flex-col gap-2 xs:gap-3 sm:gap-4"
          >
            {column.map((upload) => (
              <div
                key={upload.id}
                className="relative group cursor-pointer overflow-hidden rounded-lg"
                onClick={() => handleImageClick(upload)}
              >
                <img
                  src={upload.url}
                  alt={upload.filename}
                  className="w-full h-auto object-cover transition-transform group-hover:scale-105"
                />
                
                {/* Hover overlay with buttons */}
                <div className="absolute inset-0 bg-haus-light-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity flex items-center justify-center opacity-0 group-hover:opacity-100">
                  <div className="flex space-x-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleImageClick(upload);
                      }}
                      className="p-1.5 sm:p-2 bg-haus-light-white rounded-full text-haus-light-black hover:bg-haus-cream"
                      title="View Image"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                        <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                      </svg>
                    </button>
                    
                    <button
                      onClick={(e) => handleTryOn(upload, e)}
                      className="p-1.5 sm:p-2 bg-haus-light-white rounded text-haus-dark-green hover:bg-haus-cream"
                      title="Try On"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 3a7 7 0 100 14 7 7 0 000-14zm-1 11a1 1 0 102 0V9a1 1 0 10-2 0v5z" clipRule="evenodd" />
                        <path d="M10 2a8 8 0 100 16 8 8 0 000-16zM5 10a5 5 0 1110 0 5 5 0 01-10 0z" fillRule="evenodd" clipRule="evenodd" />
                      </svg>
                    </button>
                    
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(upload);
                      }}
                      className="p-1.5 sm:p-2 bg-haus-light-white rounded text-haus-dark-brown hover:bg-haus-cream"
                      title="Delete Image"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
      
      {/* Image modal - responsive for mobile */}
      {selectedImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black bg-opacity-75" onClick={closeModal}>
          <div className="relative max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <div className="absolute top-1 right-1 sm:top-2 sm:right-2 z-10">
              <button 
                onClick={closeModal}
                className="p-1 bg-black bg-opacity-50 rounded-full text-white hover:bg-opacity-75"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 sm:h-6 sm:w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <img 
              src={selectedImage.url} 
              alt={selectedImage.filename}
              className="w-full h-auto max-h-[70vh] sm:max-h-[80vh] object-contain rounded-lg shadow-xl"
            />
            
            <div className="mt-2 p-2 sm:p-3 bg-white rounded-lg">
              <h3 className="font-medium text-gray-800 truncate text-sm sm:text-base">{selectedImage.filename}</h3>
              <div className="flex justify-between text-xs sm:text-sm text-gray-500 mt-1">
                <span>
                  {new Date(selectedImage.createdAt).toLocaleDateString()}
                </span>
                <span>
                  {Math.round(selectedImage.size / 1024)} KB
                </span>
              </div>
              
              <div className="flex justify-between mt-2 sm:mt-3">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleTryOn(selectedImage, e);
                    closeModal();
                  }}
                  className="px-2 sm:px-3 py-1 bg-haus-dark-green text-haus-light-white rounded hover:bg-haus-light-olive text-xs sm:text-sm font-medium"
                >
                  Try On
                </button>
                
                <button
                  onClick={() => handleDelete(selectedImage)}
                  className="px-2 sm:px-3 py-1 bg-haus-dark-brown text-haus-light-white rounded hover:bg-haus-light-olive text-xs sm:text-sm font-medium"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserUploadsGallery; 